import { TrimbimModel } from '@technology/web3d-plugin-trimbim';
import { isTrbModelName } from '@trimble/web3d-providers';
import {
  createEffect,
  createMemo,
  createResource,
  createRoot,
  createSignal,
  onCleanup,
} from 'solid-js';
import type { Accessor, Setter } from 'solid-js';
import { CapabilityCoordinatorBase, CapabilityRegistry } from '@trimble/web3d';
import { Web3DViewer } from '@technology/web3d';
import type { SelectedByViewerMap } from '@trimble/web3d-providers';

/**
 * Computes effective selection count for a Web3DViewer: whole model selected = 1, else sum of entity count.
 */
async function computeSelectionCountForViewer(
  selectionMap: Map<string, number[]>,
  viewer: Web3DViewer
): Promise<number> {
  if (selectionMap.size === 0) return 0;

  let sum = 0;
  for (const [modelId, entityIds] of selectionMap) {
    const model = viewer.getModel(modelId);
    if (!model) continue;

    if (isTrbModelName(modelId) && model instanceof TrimbimModel) {
      const entities = await model.getEntities();
      const total = entities.length;
      // If all entities are selected, count as 1, otherwise count as the number of entities selected
      if (total > 0 && entityIds.length >= total) {
        sum += 1;
      } else {
        sum += entityIds.length;
      }
    } else {
      sum += 1;
    }
  }
  return sum;
}

/**
 * Merges per-viewer selection maps into a single map (modelId → union of entity IDs across Web3DViewers).
 */
function mergeSelectionByViewer(
  byViewer: SelectedByViewerMap
): Map<string, number[]> {
  const merged = new Map<string, number[]>();
  for (const viewerMap of byViewer.values()) {
    for (const [modelId, entityIds] of viewerMap) {
      const existing = merged.get(modelId) ?? [];
      const combined = new Set([...existing, ...entityIds]);
      merged.set(modelId, [...combined]);
    }
  }
  return merged;
}

export class SelectionSyncCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  /** Unique selected model ids from all Web3DViewers. */
  public readonly selectedModelIds: Accessor<Set<string>>;

  /** Per-viewer effective selection count keyed by Web3DViewer: whole model = 1, else sum of entities. */
  public readonly selectionCountByViewer: Accessor<Map<Web3DViewer, number>>;

  /** Per-viewer selection maps keyed by Web3DViewer. */
  public readonly selectedByViewer: Accessor<SelectedByViewerMap>;
  private readonly setSelectedByViewer: Setter<SelectedByViewerMap>;

  private disposeRoot: (() => void) | undefined;

  constructor(registry: CapabilityRegistry) {
    super(registry);

    const [byViewer, setByViewer] = createSignal<SelectedByViewerMap>(
      new Map()
    );
    this.selectedByViewer = byViewer;
    this.setSelectedByViewer = setByViewer;

    const mergedMap = createMemo(() =>
      mergeSelectionByViewer(this.selectedByViewer())
    );

    this.selectedModelIds = createMemo(() => {
      const map = mergedMap();
      return new Set(map.keys());
    });

    const [countResource] = createResource(
      this.selectedByViewer,
      async (byViewer): Promise<Map<Web3DViewer, number>> => {
        const counts = new Map<Web3DViewer, number>();
        for (const [viewer, map] of byViewer) {
          const count = await computeSelectionCountForViewer(map, viewer);
          counts.set(viewer, count);
        }
        return counts;
      }
    );

    this.selectionCountByViewer = createMemo(
      () => countResource() ?? new Map<Web3DViewer, number>()
    );

    this.disposeRoot = createRoot((dispose) => {
      createEffect(() => {
        const viewers = this.getViewers();
        const cleanups: Array<() => void> = [];

        this.setSelectedByViewer((prev) => {
          const next = new Map(prev);
          for (const key of next.keys()) {
            if (!viewers.includes(key)) next.delete(key);
          }
          return next;
        });

        viewers.forEach((viewer) => {
          const handler = (): void => {
            const selectionMap = new Map<string, number[]>();
            for (const [modelId, entityIds] of viewer.selection.entries()) {
              // Only track Trimbim models; viewer only highlights those (pointcloud selection is ignored)
              if (!isTrbModelName(modelId)) continue;
              selectionMap.set(modelId, [...entityIds]);
            }
            // eslint-disable-next-line sonarjs/no-nested-functions -- nested callback is the natural shape for this reactive update
            this.setSelectedByViewer((prev) => {
              const next = new Map(prev);
              next.set(viewer, selectionMap);
              return next;
            });
          };

          viewer.addEventListener('selection', handler);
          handler();

          cleanups.push(() => {
            viewer.removeEventListener('selection', handler);
          });
        });

        onCleanup(() => {
          cleanups.forEach((fn) => fn());
        });
      });
      return dispose;
    });
  }

  /**
   * Add to selection in all Web3DViewers (e.g. when user selects a tree item with Ctrl/Shift).
   */
  public async addToSelection(modelId: string): Promise<void> {
    const viewers = this.getViewers();
    for (const viewer of viewers) {
      const entityIds = await this.resolveSelectionEntityIds(
        viewer,
        modelId,
        'allEntities'
      );
      if (entityIds === undefined) continue;
      viewer.selection.add(modelId, entityIds);
    }
  }

  /**
   * Add model hierarchy roots to selection in all Web3DViewers.
   * For TRB models this mirrors tcweb model-tree behavior by preferring the
   * first hierarchy level. Falls back to all entities if no hierarchy roots are
   * available.
   */
  public async addToSelectionHierarchyRoots(modelId: string): Promise<void> {
    const viewers = this.getViewers();
    for (const viewer of viewers) {
      const entityIds = await this.resolveSelectionEntityIds(
        viewer,
        modelId,
        'hierarchyRoots'
      );
      if (entityIds === undefined) continue;
      viewer.selection.add(modelId, entityIds);
    }
  }

  /**
   * Set selection in all Web3DViewers (e.g. when user selects a tree item).
   */
  public async setSelection(modelId: string): Promise<void> {
    const viewers = this.getViewers();
    for (const viewer of viewers) {
      viewer.selection.clear();
      const entityIds = await this.resolveSelectionEntityIds(
        viewer,
        modelId,
        'allEntities'
      );
      if (entityIds === undefined) continue;
      viewer.selection.set(modelId, entityIds);
    }
  }

  /**
   * Set model hierarchy roots as selection in all Web3DViewers.
   * For TRB models this mirrors tcweb model-tree behavior by preferring the
   * first hierarchy level. Falls back to all entities if no hierarchy roots are
   * available.
   */
  public async setSelectionHierarchyRoots(modelId: string): Promise<void> {
    const viewers = this.getViewers();
    for (const viewer of viewers) {
      viewer.selection.clear();
      const entityIds = await this.resolveSelectionEntityIds(
        viewer,
        modelId,
        'hierarchyRoots'
      );
      if (entityIds === undefined) continue;
      viewer.selection.set(modelId, entityIds);
    }
  }

  public async cleanup(): Promise<void> {
    this.disposeRoot?.();
    this.disposeRoot = undefined;
  }

  private getViewers(): Web3DViewer[] {
    return this.registry
      .paneContainers()
      .map((pane) => pane.viewerWrapper()?.viewer)
      .filter((viewer) => viewer !== undefined);
  }

  private async resolveSelectionEntityIds(
    viewer: Web3DViewer,
    modelId: string,
    mode: 'allEntities' | 'hierarchyRoots'
  ): Promise<number[] | undefined> {
    const model = viewer.getModel(modelId);
    if (model === undefined) return undefined;

    if (!isTrbModelName(modelId)) return [0];

    const trbModel = model as TrimbimModel;
    if (mode === 'hierarchyRoots') {
      // eslint-disable-next-line sonarjs/no-undefined-argument -- TrimbimModel forwards `arguments` to its worker, so keep the explicit argument
      const rootIds = await trbModel.getHierarchyRoots(undefined);
      if (rootIds.length > 0) return rootIds;
    }

    return (await trbModel.getEntities()).map((entity) => entity.id);
  }
}
